import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Volume2, VolumeX } from 'lucide-react';
import type { Alert } from '@shared/protocol';
import { api } from '../lib/api';
import { useAlerts } from '../lib/alerts';
import { useDocumentTitle } from '../lib/branding';
import { socket } from '../lib/socket';
import { Segmented } from '../components/Panel';
import { SeverityBadge } from '../components/SummaryPanel';
import { Notice } from '../components/Status';

type Filter = 'unacknowledged' | 'all';

export function AlertsPage() {
  useDocumentTitle('Alerts');
  const { acknowledge, muted, setMuted } = useAlerts();
  const [alerts, setAlerts] = useState<Alert[] | null>(null);
  const [filter, setFilter] = useState<Filter>('unacknowledged');
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setAlerts((await api<{ alerts: Alert[] }>('GET', `/api/alerts?limit=500${filter === 'unacknowledged' ? '&unacknowledged=true' : ''}`)).alerts);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load alerts.');
    }
  }, [filter]);

  useEffect(() => {
    void load();
    return socket.onMessage((m) => {
      if (m.type === 'alert') setAlerts((list) => (list ? [m.alert, ...list] : list));
    });
  }, [load]);

  const ack = async (ids: number[] | 'all') => {
    try {
      await acknowledge(ids);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not acknowledge.');
    }
  };

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Alerts</h1>
          <p className="muted">Alerts raised by the alert rules and by every new FAIL finding. Administrators configure rules and webhooks under Settings.</p>
        </div>
        <div className="header-actions">
          <button type="button" className="btn" onClick={() => setMuted(!muted)} aria-pressed={muted}>
            {muted ? <VolumeX size={14} aria-hidden /> : <Volume2 size={14} aria-hidden />}
            {muted ? 'Sound muted in this browser' : 'Sound on'}
          </button>
          <button type="button" className="btn btn-primary" onClick={() => void ack('all')}>
            <Check size={14} aria-hidden /> Acknowledge all
          </button>
        </div>
      </div>
      {error && <Notice level="fail">{error}</Notice>}
      <div className="card">
        <header className="card-header">
          <h3>Alert log</h3>
          <Segmented<Filter>
            label="Alerts shown"
            value={filter}
            onChange={setFilter}
            options={[
              { value: 'unacknowledged', label: 'Unacknowledged' },
              { value: 'all', label: 'All' },
            ]}
          />
        </header>
        {alerts === null ? (
          <div className="empty">Loading</div>
        ) : alerts.length === 0 ? (
          <div className="empty">{filter === 'unacknowledged' ? 'No unacknowledged alerts.' : 'No alerts yet.'}</div>
        ) : (
          <div className="table-scroll">
            <table className="table">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Severity</th>
                  <th>Stream</th>
                  <th>Message</th>
                  <th className="actions">Acknowledged</th>
                </tr>
              </thead>
              <tbody>
                {alerts.map((a) => (
                  <tr key={a.id}>
                    <td className="num nowrap">{new Date(a.at).toLocaleString('en-GB', { hour12: false })}</td>
                    <td>
                      <SeverityBadge severity={a.severity} />
                    </td>
                    <td>
                      <Link to={`/streams/${a.streamId}`}>{a.streamName}</Link>
                    </td>
                    <td>{a.message}</td>
                    <td className="actions">
                      {a.acknowledgedAt ? (
                        <span className="muted small">
                          {a.acknowledgedBy}, {new Date(a.acknowledgedAt).toLocaleTimeString('en-GB', { hour12: false })}
                        </span>
                      ) : (
                        <button type="button" className="btn btn-sm" onClick={() => void ack([a.id])}>
                          <Check size={14} aria-hidden /> Acknowledge
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
